/**
 * Authentication Store
 * 
 * Zustand store for managing authentication state
 * Handles user session, tokens, and authentication status with persistence
 * 
 * Features:
 * - Persistent storage with automatic rehydration
 * - Type-safe state management
 * - Secure token handling
 * - Session validation
 * - Integration with AuthService
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { clearApiReadCache } from '@/lib/api-read-cache';

/** The Observability "Ask about this page" dock keeps per-page threads in
 *  sessionStorage under `uzo.obs-assistant.*`; they hold tenant data, so they
 *  go with the session. */
function clearObsAssistantThreads() {
  if (typeof window === 'undefined') return;
  try {
    const keys: string[] = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const k = sessionStorage.key(i);
      if (k && k.startsWith('uzo.obs-assistant.')) keys.push(k);
    }
    keys.forEach(k => sessionStorage.removeItem(k));
  } catch {
    /* storage blocked — nothing to clear */
  }
}
import { installCsrfFetch } from '@/lib/csrf-fetch';
import { BackendUser } from '@/lib/services/auth-service';
import { logger } from '@/lib/logger';

// ============================================================================
// TYPE DEFINITIONS  
// ============================================================================

/**
 * Enhanced user interface matching backend structure
 */
export interface User extends BackendUser {
  // Additional frontend-specific user properties can go here
  lastLoginAt?: string;
  sessionInfo?: {
    loginTime: string;
    ipAddress?: string;
    userAgent?: string;
  };
}

/**
 * Authentication state interface
 */
export interface AuthState {
  // State
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  csrfToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isInitialized: boolean;
  lastActivity: number;
  sessionExpiresAt: string | null;

  // Actions
  setUser: (user: User) => void;
  setTokens: (accessToken: string, refreshToken: string, expiresIn: number) => void;
  setCsrfToken: (csrfToken: string) => void;
  login: (user: User, accessToken: string, refreshToken: string, expiresIn: number, csrfToken?: string) => void;
  logout: () => void;
  setLoading: (loading: boolean) => void;
  setInitialized: (initialized: boolean) => void;
  updateLastActivity: () => void;
  clearSession: () => void;

  // Computed
  isSessionExpired: () => boolean;
  timeUntilExpiry: () => number;
}

// ============================================================================
// AUTH STORE IMPLEMENTATION
// ============================================================================

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      // Initial state
      user: null,
      accessToken: null,
      refreshToken: null,
      csrfToken: null,
      isAuthenticated: false,
      isLoading: false,
      isInitialized: false,
      lastActivity: Date.now(),
      sessionExpiresAt: null,

      // ========================================================================
      // ACTIONS
      // ========================================================================

      /**
       * Set user information
       */
      setUser: (user: User) => {
        logger.debug('📝 [AuthStore] Setting user:', user.email);
        set({ 
          user,
          isAuthenticated: !!user,
          lastActivity: Date.now()
        });
      },

      /**
       * Set authentication tokens
       * SECURITY: Tokens stored in memory only, NOT in cookies or localStorage
       */
      setTokens: (accessToken: string, refreshToken: string, expiresIn: number) => {
        const expiresAt = new Date(Date.now() + (expiresIn * 1000)).toISOString();

        logger.debug('🔑 [AuthStore] Setting tokens in memory, expires at:', expiresAt);

        set({
          accessToken,
          refreshToken,
          sessionExpiresAt: expiresAt,
          lastActivity: Date.now()
        });

        // SECURITY: Do NOT set document.cookie with token - vulnerable to XSS
        // HttpOnly cookies are set by the backend during login
        // Middleware should rely on HttpOnly cookies, not JS-accessible cookies
      },

      /**
       * Set CSRF token for state-changing requests
       */
      setCsrfToken: (csrfToken: string) => {
        logger.debug('🛡️ [AuthStore] Setting CSRF token');
        set({ csrfToken });
      },

      /**
       * Complete login process
       * Sets user, tokens, and authentication status
       * SECURITY: Tokens stored in memory only, NOT in cookies or localStorage
       */
      login: (user: User, accessToken: string, refreshToken: string, expiresIn: number, csrfToken?: string) => {
        const expiresAt = new Date(Date.now() + (expiresIn * 1000)).toISOString();
        const loginTime = new Date().toISOString();

        // Enhanced user object with session info
        const enhancedUser: User = {
          ...user,
          lastLoginAt: loginTime,
          sessionInfo: {
            loginTime,
            ipAddress: undefined, // Could be populated from login response
            userAgent: typeof window !== 'undefined' ? navigator.userAgent : undefined
          }
        };

        logger.debug('🎯 [AuthStore] Complete login for user:', user.email);
        if (csrfToken) {
          logger.debug('🛡️ [AuthStore] CSRF token received');
        }

        set({
          user: enhancedUser,
          accessToken,
          refreshToken,
          csrfToken: csrfToken || null,
          isAuthenticated: true,
          isLoading: false,
          lastActivity: Date.now(),
          sessionExpiresAt: expiresAt
        });

        // SECURITY: Do NOT set document.cookie with token - vulnerable to XSS
        // HttpOnly cookies are set by the backend during login response
      },

      /**
       * Logout user and clear all auth data
       * SECURITY: Only clears in-memory state; HttpOnly cookies cleared by backend
       */
      logout: () => {
        logger.debug('👋 [AuthStore] Logging out user - Clearing State');
        clearApiReadCache();
        clearObsAssistantThreads();

        // Clear persisted data from localStorage FIRST (before set() triggers persist re-write)
        if (typeof window !== 'undefined') {
          try {
            localStorage.removeItem('auth-storage');
          } catch (e) {
            logger.warn('Failed to clear local storage:', e);
          }
        }

        // Reset state to initial values (clears tokens from memory)
        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          csrfToken: null,
          isAuthenticated: false,
          isLoading: false,
          sessionExpiresAt: null,
          lastActivity: Date.now(),
          isInitialized: true // Keep initialized so we don't trigger loaders unnecessarily
        });

        // Clear again after set() in case persist middleware re-wrote it
        if (typeof window !== 'undefined') {
          try {
            // Use setTimeout to ensure this runs after any synchronous persist callbacks
            setTimeout(() => localStorage.removeItem('auth-storage'), 0);
          } catch (e) {
            logger.warn('Failed to clear local storage (deferred):', e);
          }
        }
        // NOTE: HttpOnly cookies are cleared by middleware (logout=1) and /api/auth/logout
      },

      /**
       * Set loading state
       */
      setLoading: (loading: boolean) => {
        set({ isLoading: loading });
      },

      /**
       * Set initialization state
       */
      setInitialized: (initialized: boolean) => {
        set({ isInitialized: initialized });
      },

      /**
       * Update last activity timestamp
       */
      updateLastActivity: () => {
        set({ lastActivity: Date.now() });
      },

      /**
       * Clear session data (for security)
       * SECURITY: Only clears in-memory state
       */
      clearSession: () => {
        logger.debug('🧹 [AuthStore] Clearing session data');
        clearApiReadCache();
        clearObsAssistantThreads();
        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          csrfToken: null,
          isAuthenticated: false,
          sessionExpiresAt: null
        });
        // NOTE: HttpOnly cookies are cleared by the backend
      },

      // ========================================================================
      // COMPUTED PROPERTIES
      // ========================================================================

      /**
       * Check if current session is expired
       */
      isSessionExpired: (): boolean => {
        const { sessionExpiresAt, isAuthenticated, accessToken } = get();
        
        // If not authenticated or no token, don't consider it "expired" 
        // (it's just not authenticated)
        if (!isAuthenticated || !accessToken) return false;
        
        // If no expiry set but we have a token, assume it's valid
        if (!sessionExpiresAt) return false;
        
        const now = new Date();
        const expiryTime = new Date(sessionExpiresAt);
        return expiryTime <= now;
      },

      /**
       * Get time until session expiry (in milliseconds)
       */
      timeUntilExpiry: (): number => {
        const { sessionExpiresAt } = get();
        if (!sessionExpiresAt) return 0;
        
        const now = new Date();
        const expiryTime = new Date(sessionExpiresAt);
        return Math.max(0, expiryTime.getTime() - now.getTime());
      }
    }),
    {
      name: 'auth-storage',
      
      /**
       * Partial persistence - persist only non-sensitive auth data
       * SECURITY: access_token NOT persisted to localStorage to prevent XSS attacks
       * HttpOnly cookies handle session persistence; access tokens stay in memory only
       *
       * CSRF token IS persisted because:
       * 1. It's useless without the HttpOnly session cookie (which can't be stolen via XSS)
       * 2. It prevents page refresh from breaking state-changing requests
       */
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
        sessionExpiresAt: state.sessionExpiresAt,
        lastActivity: state.lastActivity,
        csrfToken: state.csrfToken, // Persisted - requires HttpOnly cookie to be useful
        // SECURITY: DO NOT persist access_token to localStorage - XSS vulnerability
        // Access tokens are kept in memory only; HttpOnly cookies handle session persistence
        // WebSocket auth uses secure ticket exchange (see useWorkflowWebSocket.ts)
      }),

      /**
       * Custom storage with error handling and SSR support
       */
      storage: {
        getItem: (name: string) => {
          // SSR guard - return null if not in browser
          if (typeof window === 'undefined') {
            return null;
          }

          try {
            const item = localStorage.getItem(name);
            return item ? JSON.parse(item) : null;
          } catch (error) {
            logger.error('❌ [AuthStore] Failed to get item from storage:', error);
            return null;
          }
        },
        setItem: (name: string, value: any) => {
          // SSR guard - do nothing if not in browser
          if (typeof window === 'undefined') {
            return;
          }

          try {
            localStorage.setItem(name, JSON.stringify(value));
          } catch (error) {
            logger.error('❌ [AuthStore] Failed to set item in storage:', error);
          }
        },
        removeItem: (name: string) => {
          // SSR guard - do nothing if not in browser
          if (typeof window === 'undefined') {
            return;
          }

          try {
            localStorage.removeItem(name);
          } catch (error) {
            logger.error('❌ [AuthStore] Failed to remove item from storage:', error);
          }
        }
      },

      /**
       * Version for migration handling
       */
      version: 1,

      /**
       * Skip automatic localStorage hydration on first render. The store
       * starts with its declared initial state on both server and client,
       * so React's hydration sees a matching tree. `AuthProvider` calls
       * `useAuthStore.persist.rehydrate()` after mount, swapping in the
       * persisted user/session — at which point components re-render
       * normally without tripping React's hydration check.
       *
       * Without this flag, persist reads localStorage synchronously during
       * the first client render and the resulting tree differs from the
       * SSR output (React #418). Symptom on /chat: "Expected server HTML
       * to contain a matching <button> in <div>" — auth-gated buttons
       * appear on the client but not on the server.
       */
      skipHydration: true,

      /**
       * Migration function for version updates
       */
      migrate: (persistedState: any, version: number) => {
        if (version === 0) {
          // Migrate from v0 to v1
          return {
            ...persistedState,
            isInitialized: false,
            lastActivity: Date.now()
          };
        }
        return persistedState;
      }
    }
  )
);

// ============================================================================
// STORE UTILITIES
// ============================================================================

/**
 * The CSRF token to send right now.
 *
 * The token belongs to the server session named by the shared `session_id`
 * cookie, and every tab shares that cookie. Each tab, however, keeps its own
 * in-memory copy of the store — so after a sign-in in tab B, tab A still held
 * the old token, sent it with the new cookie, got a CSRF 403 on refresh, and
 * logged out, which cleared the cookies and killed tab B's fresh session too
 * ("Your session expired" a minute after signing in). Read the persisted copy,
 * which the latest sign-in in any tab wrote, and fall back to memory.
 */
export const currentCsrfToken = (): string | null => {
  if (typeof window !== 'undefined') {
    try {
      const raw = window.localStorage.getItem('auth-storage');
      const persisted = raw ? JSON.parse(raw)?.state?.csrfToken : null;
      if (persisted) {
        if (persisted !== useAuthStore.getState().csrfToken) useAuthStore.setState({ csrfToken: persisted });
        return persisted;
      }
    } catch { /* fall back to memory */ }
  }
  return useAuthStore.getState().csrfToken;
};

// Another tab signed in or out: take its auth state instead of acting on a
// stale copy (refresh timers, CSRF headers, "who am I").
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key !== 'auth-storage') return;
    try { (useAuthStore as any).persist?.rehydrate?.(); } catch { /* keep current state */ }
  });
  // IAM-10: raw fetch() calls to /backend-api with POST/PUT/PATCH/DELETE carry
  // the CSRF token too, as apiClient's calls already do (see csrf-fetch.ts).
  installCsrfFetch(currentCsrfToken);
}

/**
 * Get auth state outside of React components
 */
export const getAuthState = () => useAuthStore.getState();

/**
 * Subscribe to auth state changes outside of React components
 */
export const subscribeToAuthStore = (callback: (state: AuthState) => void) => {
  return useAuthStore.subscribe(callback);
};
